import { Chart } from 'chart.js';
import { ActiveElement } from 'chart.js';

export type XY = [number, number];

export interface ZoomToggle {
  pan: boolean;
  wheel: boolean;
  pinch: boolean;
}

export class CutChartView {
  private savedEvents?: string[];

  constructor(
    private chart: Chart,
    private makeSlopeDataset: (data: XY[], editable: boolean) => any,
  ) {}

  // --- chart.js event pipeline control ---
  disableChartEvents(): void {
    const opts: any = this.chart.options as any;

    if (!this.savedEvents) {
      const current = opts.events;
      this.savedEvents = Array.isArray(current) ? [...current] : undefined;
    }

    opts.events = [];
  }

  restoreChartEvents(): void {
    const defaults = ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'] as any;
    (this.chart.options as any).events = (this.savedEvents as any) ?? defaults;
  }

  // --- basic ops ---
  updateNone(): void {
    this.chart.update('none');
  }

  resize(): void {
    this.chart.resize();
  }

  clear(): void {
    this.chart.data.datasets = [];
    this.updateNone();
  }

  // --- slope dataset ops ---
  findSlopeDataset(): any | undefined {
    return this.chart.data.datasets.find((d: any) => d?.type === 'line');
  }

  removeSlopeDataset(): void {
    const ds = this.findSlopeDataset();
    if (!ds) return;
    const idx = this.chart.data.datasets.indexOf(ds);
    if (idx >= 0) this.chart.data.datasets.splice(idx, 1);
  }

  ensureSlopeDataset(slope: XY[], editable: boolean): void {
    const ds = this.findSlopeDataset();
    if (!ds) {
      this.chart.data.datasets.unshift(this.makeSlopeDataset(slope, editable));
    } else {
      // ✅ keep same point format as makeSlopeDataset: {x,y}
      (ds as any).data = slope.map(([x, y]) => ({ x, y }));
      (ds as any).pointRadius = editable ? 6 : 0;
      (ds as any).pointHitRadius = editable ? 12 : 0;
      (ds as any).pointHoverRadius = editable ? 12 : 0;
      (ds as any).hidden = false;
    }
  }

  setSlopePoint(i: 0 | 1, p: XY): void {
    const ds = this.findSlopeDataset();
    if (!ds) return;
    (ds as any).data[i] = { x: p[0], y: p[1] };
  }

  // --- zoom plugin toggle ---
  setZoomToggle(t: ZoomToggle): void {
    const plugins: any = (this.chart.options.plugins ??= {});
    const currentZoom: any = plugins.zoom ?? {};

    // Recreate zoom object (avoid deep mutation issues)
    plugins.zoom = {
      ...currentZoom,
      pan: { ...(currentZoom.pan ?? {}), enabled: t.pan },
      zoom: {
        ...(currentZoom.zoom ?? {}),
        wheel: { ...((currentZoom.zoom ?? {}).wheel ?? {}), enabled: t.wheel },
        pinch: { ...((currentZoom.zoom ?? {}).pinch ?? {}), enabled: t.pinch },
      },
    };
  }

  // --- hit testing ---
  hitTestSlopeEndpoint(ev: MouseEvent): 0 | 1 | null {
    const slopeIndex = this.chart.data.datasets.findIndex((d: any) => d?.type === 'line');
    if (slopeIndex < 0) return null;

    const els = this.chart.getElementsAtEventForMode(
      ev as any,
      'nearest',
      { intersect: false },
      false,
    ) as ActiveElement[];

    const el = els?.find(e => e.datasetIndex === slopeIndex);
    if (!el) return null;

    const idx = el.index;
    return (idx === 0 || idx === 1) ? (idx as 0 | 1) : null;
  }

  // --- datasets for template ---
  getDatasets(): any[] {
    return this.chart.data.datasets as any[];
  }

  toggleDataset(datasetIndex: number): void {
    const ds = this.chart.data.datasets[datasetIndex] as any;
    if (!ds) return;

    ds.hidden = !(ds.hidden ?? false);
    this.updateNone();
  }

  setDatasetVisible(datasetIndex: number, visible: boolean): void {
    const ds = this.chart.data.datasets[datasetIndex] as any;
    if (!ds) return;

    ds.hidden = !visible;
    this.updateNone();
  }

  // --- export ---
  canvasToImage(canvas: HTMLCanvasElement, filename: string): void {
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;

    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    // fond blanc
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

    // dessin du canvas original par-dessus
    ctx.drawImage(canvas, 0, 0);

    exportCanvas.toBlob((blob) => {
      if (!blob) return;

      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/ /g, '_');

      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    }, 'image/png');
  }
}
